import React, { useState, useEffect } from 'react';
import VendrLayout from '../VendrLayout';
import { useClientProfile } from '../context/ClientProfileContext';
import { useAuth } from '../../context/AuthContext';
import { profileToFormState } from '../utils/profileForm';
import './VendrSetup.css';
import './VendrSettings.css';

const VendrSettings = () => {
  const { profile, loading, saveProfile, emptyProfile } = useClientProfile();
  const { user, updatePassword } = useAuth();
  const [form, setForm] = useState(emptyProfile);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);

  useEffect(() => {
    const next = profileToFormState(profile);
    if (next) setForm(next);
  }, [profile]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setForm((prev) => {
        const next = { ...prev, [name]: checked };
        if (name === 'ship_from_same_as_address' && checked) {
          next.ship_from_address_line_1 = prev.address_line_1;
          next.ship_from_address_line_2 = prev.address_line_2 ?? '';
          next.ship_from_address_line_3 = prev.address_line_3 ?? '';
          next.ship_from_town_city = prev.town_city;
          next.ship_from_postcode = prev.postcode;
          next.ship_from_country = prev.country;
        }
        return next;
      });
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setSaving(true);
    const payload = { ...form };
    if (payload.ship_from_same_as_address) {
      payload.ship_from_address_line_1 = null;
      payload.ship_from_address_line_2 = null;
      payload.ship_from_address_line_3 = null;
      payload.ship_from_town_city = null;
      payload.ship_from_postcode = null;
      payload.ship_from_country = null;
    }
    const result = await saveProfile(payload);
    setSaving(false);
    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error || 'Failed to save');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }
    setPasswordSubmitting(true);
    const result = await updatePassword(newPassword);
    setPasswordSubmitting(false);
    if (result.success) {
      setPasswordSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setPasswordError(result.error || 'Failed to update password.');
    }
  };

  if (loading) {
    return (
      <VendrLayout>
        <div className="vendr-setup-loading">Loading...</div>
      </VendrLayout>
    );
  }

  return (
    <VendrLayout>
      <div className="vendr-settings">
        <h1 className="vendr-setup-title">Settings</h1>
        <p className="vendr-setup-intro">
          Update your company and contact details. Your login email is managed by your account.
        </p>

        <section className="vendr-setup-section">
          <h2>User information</h2>
          <div className="vendr-setup-grid">
            <div className="vendr-setup-field vendr-setup-field-full">
              <label htmlFor="login_email">Login email</label>
              <input
                id="login_email"
                type="email"
                value={user?.email ?? ''}
                readOnly
                disabled
                className="vendr-settings-readonly"
              />
              <span className="vendr-settings-readonly-hint">To change your login email, please contact support.</span>
            </div>
          </div>
        </section>

        <section className="vendr-setup-section">
          <h2>Password</h2>
          <form onSubmit={handlePasswordSubmit} className="vendr-settings-password-form">
            {passwordError && <div className="vendr-setup-error">{passwordError}</div>}
            {passwordSuccess && <div className="vendr-settings-success">Your password has been updated.</div>}
            <div className="vendr-setup-grid">
              <div className="vendr-setup-field">
                <label htmlFor="new_password">New password</label>
                <input
                  id="new_password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={6}
                  autoComplete="new-password"
                  placeholder="At least 6 characters"
                />
              </div>
              <div className="vendr-setup-field">
                <label htmlFor="confirm_password">Confirm new password</label>
                <input
                  id="confirm_password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>
            </div>
            <div className="vendr-settings-password-actions">
              <button type="submit" className="vendr-setup-submit" disabled={passwordSubmitting}>
                {passwordSubmitting ? 'Updating…' : 'Update password'}
              </button>
            </div>
          </form>
        </section>

        <form onSubmit={handleSubmit} className="vendr-setup-form">
          {error && <div className="vendr-setup-error">{error}</div>}
          {success && <div className="vendr-settings-success">Your settings have been saved.</div>}

          <section className="vendr-setup-section">
            <h2>Primary contact details</h2>
            <div className="vendr-setup-grid">
              <div className="vendr-setup-field">
                <label htmlFor="first_name">First name *</label>
                <input
                  id="first_name"
                  name="first_name"
                  value={form.first_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="vendr-setup-field">
                <label htmlFor="last_name">Last name *</label>
                <input
                  id="last_name"
                  name="last_name"
                  value={form.last_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="vendr-setup-field">
                <label htmlFor="job_title">Job title *</label>
                <input
                  id="job_title"
                  name="job_title"
                  value={form.job_title}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="vendr-setup-field">
                <label htmlFor="company_name">Company name *</label>
                <input
                  id="company_name"
                  name="company_name"
                  value={form.company_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="vendr-setup-field">
                <label htmlFor="email">Contact email *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="vendr-setup-field">
                <label htmlFor="mobile_phone">Mobile phone *</label>
                <input
                  id="mobile_phone"
                  name="mobile_phone"
                  type="tel"
                  value={form.mobile_phone}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="vendr-setup-field vendr-setup-field-full">
                <label htmlFor="secondary_phone">Secondary phone</label>
                <input
                  id="secondary_phone"
                  name="secondary_phone"
                  type="tel"
                  value={form.secondary_phone}
                  onChange={handleChange}
                />
              </div>
            </div>
          </section>

          <section className="vendr-setup-section">
            <h2>Company / supplier details</h2>
            <div className="vendr-setup-grid">
              <div className="vendr-setup-field vendr-setup-field-full">
                <label htmlFor="brand_supplier_name">Brand / supplier name *</label>
                <input
                  id="brand_supplier_name"
                  name="brand_supplier_name"
                  value={form.brand_supplier_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="vendr-setup-field vendr-setup-field-full">
                <label htmlFor="address_line_1">Address line 1 *</label>
                <input
                  id="address_line_1"
                  name="address_line_1"
                  value={form.address_line_1}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="vendr-setup-field vendr-setup-field-full">
                <label htmlFor="address_line_2">Address line 2</label>
                <input
                  id="address_line_2"
                  name="address_line_2"
                  value={form.address_line_2}
                  onChange={handleChange}
                />
              </div>
              <div className="vendr-setup-field vendr-setup-field-full">
                <label htmlFor="address_line_3">Address line 3</label>
                <input
                  id="address_line_3"
                  name="address_line_3"
                  value={form.address_line_3}
                  onChange={handleChange}
                />
              </div>
              <div className="vendr-setup-field">
                <label htmlFor="town_city">Town / city *</label>
                <input
                  id="town_city"
                  name="town_city"
                  value={form.town_city}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="vendr-setup-field">
                <label htmlFor="postcode">Postcode *</label>
                <input
                  id="postcode"
                  name="postcode"
                  value={form.postcode}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="vendr-setup-field">
                <label htmlFor="country">Country *</label>
                <input
                  id="country"
                  name="country"
                  value={form.country}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </section>

          <section className="vendr-setup-section vendr-setup-section-ship-from">
            <h2>Ship from address</h2>
            <div className="vendr-setup-same-as-wrap">
              <label className="vendr-setup-checkbox-label">
                <input
                  type="checkbox"
                  name="ship_from_same_as_address"
                  checked={!!form.ship_from_same_as_address}
                  onChange={handleChange}
                />
                <span>Same as company address</span>
              </label>
            </div>
            {!form.ship_from_same_as_address && (
              <div className="vendr-setup-grid">
                <div className="vendr-setup-field vendr-setup-field-full">
                  <label htmlFor="ship_from_address_line_1">Address line 1 *</label>
                  <input
                    id="ship_from_address_line_1"
                    name="ship_from_address_line_1"
                    value={form.ship_from_address_line_1 ?? ''}
                    onChange={handleChange}
                    required={!form.ship_from_same_as_address}
                  />
                </div>
                <div className="vendr-setup-field vendr-setup-field-full">
                  <label htmlFor="ship_from_address_line_2">Address line 2</label>
                  <input
                    id="ship_from_address_line_2"
                    name="ship_from_address_line_2"
                    value={form.ship_from_address_line_2 ?? ''}
                    onChange={handleChange}
                  />
                </div>
                <div className="vendr-setup-field vendr-setup-field-full">
                  <label htmlFor="ship_from_address_line_3">Address line 3</label>
                  <input
                    id="ship_from_address_line_3"
                    name="ship_from_address_line_3"
                    value={form.ship_from_address_line_3 ?? ''}
                    onChange={handleChange}
                  />
                </div>
                <div className="vendr-setup-field">
                  <label htmlFor="ship_from_town_city">Town / city *</label>
                  <input
                    id="ship_from_town_city"
                    name="ship_from_town_city"
                    value={form.ship_from_town_city ?? ''}
                    onChange={handleChange}
                    required={!form.ship_from_same_as_address}
                  />
                </div>
                <div className="vendr-setup-field">
                  <label htmlFor="ship_from_postcode">Postcode *</label>
                  <input
                    id="ship_from_postcode"
                    name="ship_from_postcode"
                    value={form.ship_from_postcode ?? ''}
                    onChange={handleChange}
                    required={!form.ship_from_same_as_address}
                  />
                </div>
                <div className="vendr-setup-field">
                  <label htmlFor="ship_from_country">Country *</label>
                  <input
                    id="ship_from_country"
                    name="ship_from_country"
                    value={form.ship_from_country ?? ''}
                    onChange={handleChange}
                    required={!form.ship_from_same_as_address}
                  />
                </div>
              </div>
            )}
          </section>

          <div className="vendr-setup-actions">
            <button type="submit" className="vendr-setup-submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </VendrLayout>
  );
};

export default VendrSettings;
